/**
 * Brain Health V5.0 Admin Dashboard
 * System-wide analytics for brain health tracking
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Psychology as BrainIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import MetricCard from '../common/MetricCard';
import StatusBadge from '../common/StatusBadge';
import { colors, spacing } from '../../theme/designTokens';

interface SystemStats {
  users: {
    total_users: string;
    avg_overall_score: string;
    avg_cognitive_score: string;
    excellent_users: string;
    very_good_users: string;
    good_users: string;
    needs_attention_users: string;
  };
  chapters: {
    total_chapters: string;
    avg_chapter_score: string;
    exceptional_chapters: string;
    high_impact_chapters: string;
    moderate_chapters: string;
  };
  activities: {
    total_activities: string;
    active_users: string;
    avg_points: string;
    chapter_activities: string;
    voice_activities: string;
    photo_activities: string;
  };
}

interface UserBrainHealth {
  user_id: number;
  email: string;
  username: string;
  overall_score: number;
  cognitive_engagement_score: number;
  chapters_created: number;
  words_written: number;
  strongest_domain: string;
  trend_direction: string;
}

const BrainHealthDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [topUsers, setTopUsers] = useState<UserBrainHealth[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load system stats and top users
      const [statsResponse, usersResponse] = await Promise.all([
        apiService.get('/api/admin/brain-health/system-stats'),
        apiService.get('/api/admin/brain-health/users?limit=20&sortBy=overall_score&sortOrder=DESC')
      ]);

      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      }

      if (usersResponse.data.success) {
        setTopUsers(usersResponse.data.data);
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Failed to load brain health data:', err);
      setError(err.message || 'Failed to load dashboard data');
      setLoading(false);
      dispatch(addNotification({
        message: 'Failed to load brain health data',
        severity: 'error'
      }));
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#10B981'; // Green
    if (score >= 70) return '#84CC16'; // Light Green
    if (score >= 60) return '#FBBF24'; // Yellow
    if (score >= 50) return '#F59E0B'; // Orange
    return '#EF4444'; // Red
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 80) return 'Excellent';
    if (score >= 70) return 'Very Good';
    if (score >= 60) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Needs Attention';
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'improving') return <TrendingUpIcon sx={{ color: '#10B981', fontSize: 20 }} />;
    if (trend === 'declining') return <TrendingDownIcon sx={{ color: '#EF4444', fontSize: 20 }} />;
    return <span style={{ fontSize: 16 }}>➡️</span>;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button onClick={loadDashboardData} size="small">Retry</Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: spacing[8] }}>
        <Box>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: spacing[3],
              mb: spacing[2]
            }}
          >
            <BrainIcon sx={{ fontSize: 40, color: colors.content.main }} />
            Brain Health V5.0 Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
            System-wide cognitive wellness tracking and insights
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadDashboardData}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: spacing[6] }}>
          <Grid item xs={12} md={4}>
            <MetricCard
              title="Total Users"
              value={stats.users.total_users}
              subtitle={`Avg Score: ${stats.users.avg_overall_score} / 100`}
              icon="people"
              color="content"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              title="Total Chapters"
              value={stats.chapters.total_chapters}
              subtitle={`Avg Impact: ${stats.chapters.avg_chapter_score} / 100`}
              icon="article"
              color="content"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              title="Activities (30 days)"
              value={stats.activities.total_activities}
              subtitle={`${stats.activities.active_users} active users`}
              icon="timeline"
              color="content"
            />
          </Grid>
        </Grid>
      )}

      {/* Top Users Leaderboard */}
      <Paper sx={{ p: spacing[4] }}>
        <Typography variant="h6" sx={{ mb: spacing[4], fontWeight: 600 }}>
          Top Performing Users
        </Typography>
        <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Overall Score</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Chapters</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Words</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Strongest Domain</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Trend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topUsers.map((user, index) => (
              <TableRow key={user.user_id} hover>
                <TableCell>
                  <Chip
                    label={`#${index + 1}`}
                    size="small"
                    color={index < 3 ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {user.username || user.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${user.overall_score} - ${getScoreLabel(user.overall_score)}`}
                    size="small"
                    sx={{
                      backgroundColor: getScoreColor(user.overall_score),
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell>{user.chapters_created}</TableCell>
                <TableCell>{user.words_written.toLocaleString()}</TableCell>
                <TableCell>
                  <StatusBadge
                    status="default"
                    label={user.strongest_domain}
                    variant="outlined"
                    size="sm"
                  />
                </TableCell>
                <TableCell>
                  {getTrendIcon(user.trend_direction)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>

        {topUsers.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No brain health data available yet
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BrainHealthDashboard;
